import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  Typography,
  fontStacks,
  useConfirm,
} from '@munifocus/ui';
import type { AppealDto, AppealHearingDto, AppealStatus } from '@munifocus/contracts-gov';
import { useAcceptAppeal, useAppeals, useWithdrawAppeal } from '../../lib/appeals-api';
import { formatDate, formatDateTime } from '../../lib/relative-time';
import {
  DecideAppealDialog,
  RecordAppealDialog,
  RecordHearingDialog,
  RejectAppealDialog,
  ScheduleHearingDialog,
} from './AppealDialogs';

const STATUS_TONE: Record<
  AppealStatus,
  'pending' | 'warning' | 'success' | 'rejected' | 'approved'
> = {
  submitted: 'warning',
  accepted: 'pending',
  scheduled: 'pending',
  heard: 'pending',
  decided: 'success',
  rejected: 'rejected',
  withdrawn: 'rejected',
};

/**
 * Appeals of the Notice to Comply to the Appeal Committee (LGA s. 134): what
 * was filed, the hearing, and the decision.
 *
 * Rendered only where the municipality has the module. What a row offers
 * follows the appeal's state and the caller's permissions on the file —
 * `appeal:manage` to accept, schedule and record the hearing, `appeal:decide`
 * to enter the committee's decision — and the API re-checks both.
 */
export function AppealsPanel({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const appeals = useAppeals(fileId);
  const accept = useAcceptAppeal(fileId);
  const withdraw = useWithdrawAppeal(fileId);
  const confirm = useConfirm();
  const [recording, setRecording] = useState(false);
  const [rejecting, setRejecting] = useState<AppealDto | null>(null);
  const [scheduling, setScheduling] = useState<AppealDto | null>(null);
  const [deciding, setDeciding] = useState<AppealDto | null>(null);
  const [hearing, setHearing] = useState<{ appealId: string; hearingId: string } | null>(null);

  const data = appeals.data;
  if (!data?.enabled) return null;
  const open = data.appeals.some((a) =>
    ['submitted', 'accepted', 'scheduled', 'heard'].includes(a.status),
  );

  const onWithdraw = async (appeal: AppealDto) => {
    const ok = await confirm({
      title: t('gov.appeals.withdraw.title'),
      body: t('gov.appeals.withdraw.body'),
      confirmLabel: t('gov.appeals.withdraw.action'),
      cancelLabel: t('common.actions.cancel'),
    });
    if (ok) withdraw.mutate(appeal.id);
  };

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 16 }}
            >
              {t('gov.appeals.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.windowClosesOn
                ? t('gov.appeals.window', {
                    date: formatDate(data.windowClosesOn, i18n.language),
                  })
                : t('gov.appeals.notServed')}
            </Typography>
          </Box>
          {data.canManage && data.serviceDate && !open && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Icons.Gavel size={iconSize.sm} />}
              onClick={() => setRecording(true)}
            >
              {t('gov.appeals.record.action')}
            </Button>
          )}
        </Stack>

        {appeals.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} thickness={5} sx={{ color: palette.brand.accent }} />
          </Box>
        )}
        {appeals.isError && <Alert severity="error">{t('gov.appeals.loadError')}</Alert>}
        {data.appeals.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.appeals.empty')}
          </Typography>
        )}

        {data.appeals.map((appeal) => (
          <HairlineCard key={appeal.id} sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {t('gov.appeals.number', { number: appeal.sequence })}
                </Typography>
                <StatusChip
                  status={STATUS_TONE[appeal.status]}
                  label={t(`gov.appeals.status.${appeal.status}`)}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(`gov.appeals.source.${appeal.source}`)}
                </Typography>
                {appeal.withinWindow === false && (
                  <StatusChip status="warning" label={t('gov.appeals.late')} />
                )}
              </Stack>

              <Typography variant="body2">
                {t('gov.appeals.filedBy', {
                  name: appeal.appellantName,
                  role: t(`gov.appeals.role.${appeal.appellantRole}`),
                  date: formatDateTime(appeal.filedAt, i18n.language),
                })}
              </Typography>
              <Box>
                <Typography variant="overline">{t('gov.appeals.fields.grounds')}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {appeal.grounds}
                </Typography>
              </Box>
              {appeal.reliefSought && (
                <Box>
                  <Typography variant="overline">{t('gov.appeals.fields.reliefSought')}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {appeal.reliefSought}
                  </Typography>
                </Box>
              )}
              {appeal.rejectionReason && (
                <Alert severity="info">
                  {t('gov.appeals.rejectedBecause', { reason: appeal.rejectionReason })}
                </Alert>
              )}

              {appeal.hearings.length > 0 && <Divider flexItem />}
              {appeal.hearings.map((h) => (
                <HearingRow
                  key={h.id}
                  hearing={h}
                  canRecord={
                    data.canManage && h.status === 'scheduled' && appeal.status === 'scheduled'
                  }
                  onRecord={() => setHearing({ appealId: appeal.id, hearingId: h.id })}
                />
              ))}

              {appeal.decision && (
                <>
                  <Divider flexItem />
                  <Stack spacing={0.5}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t(`gov.appeals.decision.${appeal.decision}`)}
                      {appeal.decisionVariation
                        ? ` — ${t(`gov.appeals.variation.${appeal.decisionVariation}`)}`
                        : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('gov.appeals.decidedOn', {
                        date: formatDate(appeal.decisionDate, i18n.language),
                        name: appeal.decidedByName ?? '—',
                      })}
                    </Typography>
                    {appeal.revisedComplianceDeadline && (
                      <Typography variant="body2">
                        {t('gov.appeals.revisedDeadline', {
                          date: formatDate(appeal.revisedComplianceDeadline, i18n.language),
                        })}
                      </Typography>
                    )}
                    {appeal.decisionReasons && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {appeal.decisionReasons}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {data.canManage && appeal.status === 'submitted' && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={accept.isPending}
                      onClick={() => accept.mutate(appeal.id)}
                    >
                      {t('gov.appeals.accept')}
                    </Button>
                    <Button size="small" onClick={() => setRejecting(appeal)}>
                      {t('gov.appeals.reject.action')}
                    </Button>
                  </>
                )}
                {data.canManage && appeal.status === 'accepted' && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Icons.CalendarClock size={iconSize.sm} />}
                    onClick={() => setScheduling(appeal)}
                  >
                    {t('gov.appeals.schedule.action')}
                  </Button>
                )}
                {data.canDecide && appeal.status === 'heard' && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Icons.Gavel size={iconSize.sm} />}
                    onClick={() => setDeciding(appeal)}
                  >
                    {t('gov.appeals.decide.action')}
                  </Button>
                )}
                {data.canManage &&
                  ['submitted', 'accepted', 'scheduled', 'heard'].includes(appeal.status) && (
                    <Button size="small" onClick={() => void onWithdraw(appeal)}>
                      {t('gov.appeals.withdraw.action')}
                    </Button>
                  )}
              </Stack>
            </Stack>
          </HairlineCard>
        ))}
      </Stack>

      <RecordAppealDialog fileId={fileId} open={recording} onClose={() => setRecording(false)} />
      <RejectAppealDialog fileId={fileId} appeal={rejecting} onClose={() => setRejecting(null)} />
      <ScheduleHearingDialog
        fileId={fileId}
        appeal={scheduling}
        onClose={() => setScheduling(null)}
      />
      <RecordHearingDialog fileId={fileId} target={hearing} onClose={() => setHearing(null)} />
      <DecideAppealDialog fileId={fileId} appeal={deciding} onClose={() => setDeciding(null)} />
    </HairlineCard>
  );
}

function HearingRow({
  hearing,
  canRecord,
  onRecord,
}: {
  hearing: AppealHearingDto;
  canRecord: boolean;
  onRecord: () => void;
}) {
  const { t, i18n } = useTranslation();
  const fr = i18n.language.startsWith('fr');
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
    >
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Icons.CalendarClock size={iconSize.sm} aria-hidden />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatDateTime(hearing.scheduledFor, i18n.language)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(`gov.appeals.hearingStatus.${hearing.status}`)}
          </Typography>
        </Stack>
        <Typography variant="body2">{hearing.location}</Typography>
        <Typography variant="caption" color="text.secondary">
          {hearing.members
            .map((m) => {
              const title = fr ? m.titleFr || m.titleEn : m.titleEn || m.titleFr;
              const chair = m.role === 'chair' ? ` (${t('gov.appeals.chair')})` : '';
              return `${m.name}${title ? `, ${title}` : ''}${chair}`;
            })
            .join(' · ')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hearing.noticeSentAt
            ? t('gov.appeals.noticeSent', {
                date: formatDateTime(hearing.noticeSentAt, i18n.language),
              })
            : t('gov.appeals.noticeNotSent')}
        </Typography>
        {hearing.minutes && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {hearing.minutes}
          </Typography>
        )}
      </Stack>
      {canRecord && (
        <Button size="small" variant="outlined" onClick={onRecord} sx={{ flexShrink: 0 }}>
          {t('gov.appeals.hearing.action')}
        </Button>
      )}
    </Stack>
  );
}
